import type {
  Company,
  Site,
  PersonRecord,
  RefrigerationUnitType,
  VisitUnitCheck,
} from '@/data/schemas';
import type { ExpandableCatalog } from '@/data/models/catalog';
import { CATALOG_IDS } from '@/data/models/catalog';
import { resolveFieldValueText } from '@/data/fieldPatterns';
import {
  frequencyToMonths,
  formatVisitControlPeriod,
  formatFrequencyLabel,
  computeNextVisitDate,
  describeMonthWindow,
  type MonthWindowDescriptor,
} from '@/utils/periodicity';
import { DEFAULT_SETTINGS } from '@/data/repos/settingsRepo';
import { buildComponentsSummary, buildUnitThresholdsText } from './formatters';
import { formatIsoDate, formatIsoDateDDMMYYYY } from '@/utils/date';
import { resolveAddress } from '@/utils/address';

export function recipientsFromPersonnel(
  personnel?: PersonRecord[],
  site?: Site
): {
  list?: string;
  summary?: string;
  reperibileContacts?: string[];
} {
  if (!personnel?.length) {
    return {};
  }

  const recipients = personnel.filter(
    (person) =>
      person.reportRecipient &&
      (person.scope === 'company' || (person.scope === 'site' && person.siteId === site?.id))
  );

  if (!recipients.length) {
    return {};
  }

  const formatRecipient = (person: PersonRecord): string => {
    const lastName = person.lastName?.trim();
    if (lastName) return lastName;
    const combined = `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim();
    return combined.length ? combined : person.id;
  };

  const list = recipients
    .map(formatRecipient)
    .filter((entry) => entry.length > 0)
    .join(', ');
  const summary = recipients.slice(0, 3).map(formatRecipient).join(', ');

  const reperibileContacts: string[] = [];

  personnel
    .filter((person) => person.reperibile && person.phone)
    .forEach((person) => {
      if (person.phone) reperibileContacts.push(person.phone);
    });

  // Add site-level reperibile phone if available
  if (site?.reperibilePhone) {
    reperibileContacts.push(site.reperibilePhone);
  }

  const uniqueReperibileContacts = Array.from(new Set(reperibileContacts));

  return {
    list,
    summary,
    reperibileContacts: uniqueReperibileContacts.length > 0 ? uniqueReperibileContacts : undefined,
  };
}

function deriveAlarmReference(company?: Company): {
  contact?: string;
} {
  if (!company?.personnel?.length) {
    return {};
  }
  const reference = company.personnel.find((person) => person.alarmContact);
  return reference
    ? {
        contact: `${reference.firstName} ${reference.lastName}`,
      }
    : {};
}

export function buildCompanyContext(
  company: Company,
  nostroRecapito?: string,
  site?: Site
): Company & {
  reportRecipients?: string;
  reportRecipientsSummary?: string;
  reperibileContacts?: string[];
  ourContact?: { email?: string };
  alarmReference?: { contact?: string };
  pendingLicenses: Array<{
    id: string;
    name: string;
    status: 'expired' | 'pending' | 'complete';
    expiry?: string;
  }>;
} {
  const { list, summary, reperibileContacts } = recipientsFromPersonnel(company.personnel, site);
  const ourContactEmail = nostroRecapito ?? DEFAULT_SETTINGS.nostroRecapito;
  const alarmReference = deriveAlarmReference(company);

  const today = formatIsoDate(new Date());
  const pendingLicenses = (company.personnel || [])
    .filter((person) => person.licensed)
    .map((person) => {
      let status: 'expired' | 'pending' | 'complete' = 'complete';

      if (person.licenseExpiry) {
        const expiry = new Date(person.licenseExpiry);
        const todayDate = new Date(today);

        if (expiry < todayDate) {
          status = 'expired';
        } else if (person.licenseExpiry === 'pending' || !person.licenseExpiry) {
          status = 'pending';
        }
      } else if (person.licensed) {
        status = 'pending';
      }

      return {
        id: person.id,
        name: `${person.firstName} ${person.lastName}`,
        status,
        expiry: person.licenseExpiry ?? (status === 'pending' ? 'In attesa di data' : undefined),
      };
    });

  return {
    ...company,
    reportRecipients: list,
    reportRecipientsSummary: summary,
    reperibileContacts,
    ourContact: {
      email: ourContactEmail,
    },
    alarmReference,
    pendingLicenses,
  };
}

type SiteSchedule = {
  frequency?: Site['frequency'];
  frequencyLabel?: string;
  controlPeriod?: string;
  previousVisit?: string;
  nextVisit?: string;
  nextVisitDisplay?: string;
  frequencyMonths?: number;
  nextVisitWindow?: MonthWindowDescriptor;
};

export function buildScheduleContext(
  site: Site | undefined,
  visitDate: string,
  previousReport?: { id: string; visitDate: string } | null
): SiteSchedule | undefined {
  if (!site) return undefined;

  const frequency = site.frequency;
  const frequencyLabel = frequency ? formatFrequencyLabel(frequency) : undefined;
  const frequencyMonths = frequency ? frequencyToMonths(frequency) : undefined;
  const controlPeriod = formatVisitControlPeriod(visitDate, frequencyMonths);
  const previousVisit = previousReport?.visitDate ?? site.periodicityPreviousVisit ?? undefined;
  const manualNextVisit = site.periodicityNextVisit ?? undefined;
  const computedNextVisit = computeNextVisitDate(visitDate, frequency) ?? undefined;
  const nextVisit = manualNextVisit ?? computedNextVisit ?? undefined;
  const nextVisitWindow = describeMonthWindow(computedNextVisit ?? nextVisit);
  const nextVisitDisplay = manualNextVisit
    ? formatIsoDateDDMMYYYY(manualNextVisit)
    : (nextVisitWindow?.label ?? 'Da pianificare');

  return {
    frequency,
    frequencyLabel,
    controlPeriod,
    previousVisit,
    nextVisit,
    nextVisitDisplay,
    frequencyMonths,
    nextVisitWindow,
  };
}

function buildAuthorizationContext(site: Site): {
  quantityKg?: number;
  protocolNumber?: string;
  issueDate?: string;
  expiryDate?: string;
  prescriptions?: string;
  gazzetta: {
    published: boolean;
    date?: string;
    status: 'complete' | 'pending';
  };
} {
  const hasAuthorization = Boolean(site.authorizationProtocolNumber && site.authorizationIssueDate);
  const hasGazette = Boolean(site.officialGazettePublished && site.officialGazetteDate);

  let gazetteStatus: 'complete' | 'pending' = 'complete';
  if (hasAuthorization && site.officialGazettePublished && !hasGazette) {
    gazetteStatus = 'pending';
  }

  const computedQuantityKg = site.refrigerationUnits?.length
    ? site.refrigerationUnits
        .map((unit) => unit.ammoniaKg ?? 0)
        .reduce((sum, value) => sum + value, 0)
    : undefined;
  const quantityKg = site.authorizedNh3Kg ?? computedQuantityKg;

  return {
    quantityKg,
    protocolNumber: site.authorizationProtocolNumber,
    issueDate: site.authorizationIssueDate,
    expiryDate: site.authorizationExpiryDate,
    prescriptions: site.authorizationPrescriptions,
    gazzetta: {
      published: Boolean(site.officialGazettePublished),
      date:
        site.officialGazetteDate ??
        (site.officialGazettePublished ? 'In attesa di pubblicazione' : undefined),
      status: gazetteStatus,
    },
  };
}

function buildCpiContext(site: Site): {
  present: boolean;
  protocolNumber?: string;
  lastRenewal?: string;
  expiry?: string;
  ammoniaActivityCodes: boolean;
  status: 'complete' | 'pending';
} {
  const present = Boolean(site.cpiPresent);
  const hasExpiry = Boolean(site.cpiExpiryDate);
  const hasActivityCodes = Boolean(site.ammoniaActivityCodes);

  let status: 'complete' | 'pending' = 'complete';
  if (present) {
    status = hasExpiry && hasActivityCodes ? 'complete' : 'pending';
  }

  return {
    present,
    protocolNumber: site.cpiProtocolNumber ?? undefined,
    lastRenewal: site.cpiLastRenewalDate ?? undefined,
    expiry: site.cpiExpiryDate ?? (present ? 'In attesa di data' : undefined),
    ammoniaActivityCodes: Boolean(site.ammoniaActivityCodes),
    status,
  };
}

export function buildSiteContext(
  site: Site | undefined,
  schedule: SiteSchedule | undefined,
  catalogs?: Record<string, ExpandableCatalog>
):
  | (Site & {
      schedule?: SiteSchedule;
      locationDescription?: string;
      toxicGasLabel: string;
      authorization: ReturnType<typeof buildAuthorizationContext>;
      cpi: ReturnType<typeof buildCpiContext>;
      refrigerationUnits: Array<ReturnType<typeof buildUnitContext>>;
      refrigerationUnitsWithAbatementSystem: Array<ReturnType<typeof buildUnitContext>>;
    })
  | undefined {
  if (!site) return undefined;

  const address = resolveAddress(
    {
      address: site.sedeOperativaAddress,
      cap: site.sedeOperativaCap,
      city: site.sedeOperativaCity,
      province: site.sedeOperativaProvince,
    },
    site.sedeOperativa ?? site.address
  );
  const locationDescription = site.siteLocation ?? address;
  const toxicGasLabel = site.toxicGas ?? site.gasType ?? 'Ammoniaca Anidra';
  const unitContexts = (site.refrigerationUnits ?? []).map((unit) =>
    buildUnitContext(unit, catalogs)
  );

  const unitsWithAbatement = unitContexts.filter((unit) => {
    const safety = (unit as Record<string, unknown>).safety as
      | { abatementSystem?: { present?: boolean } }
      | undefined;
    return Boolean(safety?.abatementSystem?.present);
  });

  return {
    ...site,
    address,
    schedule,
    locationDescription,
    toxicGasLabel,
    authorization: buildAuthorizationContext(site),
    cpi: buildCpiContext(site),
    refrigerationUnits: unitContexts as unknown as Site['refrigerationUnits'],
    refrigerationUnitsWithAbatementSystem: unitsWithAbatement,
  };
}

function buildUnitContext(
  unit: RefrigerationUnitType,
  catalogs?: Record<string, ExpandableCatalog>
): Record<string, unknown> {
  const locationText = resolveFieldValueText(
    unit.location,
    catalogs?.[CATALOG_IDS.PLANT_LOCATIONS]
  );
  const toxicGasText = resolveFieldValueText(
    unit.toxicGasType,
    catalogs?.[CATALOG_IDS.TOXIC_GASES]
  );
  const impiantoTypeText = resolveFieldValueText(
    unit.impiantoType,
    catalogs?.[CATALOG_IDS.PLANT_TYPES]
  );
  const safety = unit.technical.safety;
  const ventilation = safety.ventilation;
  const natural = ventilation?.natural;
  const extractors = ventilation?.extractors;
  const valves = safety.valves;
  const positionsText = safety.detectors.positions.length
    ? safety.detectors.positions.join('\n')
    : undefined;
  const thresholdsText = buildUnitThresholdsText(unit, catalogs?.[CATALOG_IDS.THRESHOLD_ACTIONS]);
  const componentsSummary = buildComponentsSummary(unit);
  const detectionMaintenance = safety.detectionMaintenance;
  const reliefSetpoints = valves?.reliefSetPressuresBar?.length
    ? valves.reliefSetPressuresBar.map((value) => `${value} bar`).join('\n')
    : undefined;

  const ventilationContext = ventilation
    ? {
        ...ventilation,
        natural,
        extractors,
        apertureHighM2: ventilation.apertureHighM2 ?? natural?.highOpeningsAreaM2,
        apertureLowM2: ventilation.apertureLowM2 ?? natural?.lowOpeningsAreaM2,
        extractorsCount: ventilation.extractorsCount ?? extractors?.units,
        extractorsFlowM3h: ventilation.extractorsFlowM3h ?? extractors?.airflowM3h,
        extractorsOperation:
          ventilation.extractorsOperation ?? extractors?.mode ?? extractors?.logic,
        extractorsAtex: ventilation.extractorsAtex ?? extractors?.atex,
      }
    : undefined;

  const detectorsContext = {
    ...safety.detectors,
    positionsText,
    thresholdsText,
  };

  // Derive btGreaseBand from any component having it set
  const btGreaseBand = unit.technical.components.some((c) => c.btGreaseBand);

  return {
    ...unit,
    location: locationText,
    toxicGasType: toxicGasText,
    impiantoType: impiantoTypeText,
    components: unit.technical.components,
    btGreaseBand,
    maintenanceContractor: unit.technical.maintenanceContractor,
    notes: unit.technical.notes,
    componentsSummary,
    safety: {
      ...safety,
      detectors: detectorsContext,
      ventilation: ventilationContext,
    },
    verification: {
      detectors: {
        maintainer: detectionMaintenance?.name,
        maintenanceNotes: detectionMaintenance?.notes,
      },
      valves: {
        reliefSetpointsBar: reliefSetpoints,
        inspectionPeriodYears: valves?.reliefPeriodicityYears,
        nextDue: undefined,
        notes: valves?.reliefNotes,
      },
      interception: {
        present: valves?.isolationPresent ?? false,
        deviatore: valves?.isolationDiverter ?? false,
        piombatura: Boolean(valves?.isolationNotes?.toLowerCase().includes('piomb')),
      },
    },
  };
}

export function buildMetricsContext(
  site?: Site,
  schedule?: SiteSchedule,
  previousReport?: { id: string; visitDate: string } | null
):
  | {
      totalAmmoniaKg?: number;
      ammoniaWithinAuthorization?: string;
      previousVisit?: { date: string; url: string };
      nextInspectionMonth?: string;
      nextInspectionWindow?: MonthWindowDescriptor;
    }
  | undefined {
  if (!site) return undefined;

  const totalAmmoniaKg = site.refrigerationUnits?.length
    ? site.refrigerationUnits
        .map((unit) => unit.ammoniaKg ?? 0)
        .reduce((sum, value) => sum + value, 0)
    : undefined;

  const ammoniaWithinAuthorization = undefined;

  const previousVisit = previousReport
    ? {
        date: previousReport.visitDate,
        url: `/reports/${previousReport.id}`,
      }
    : undefined;

  const nextInspectionWindow = schedule?.nextVisitWindow;
  const nextInspectionMonth = nextInspectionWindow?.isoValue ?? schedule?.nextVisit;

  if (
    totalAmmoniaKg === undefined &&
    ammoniaWithinAuthorization === undefined &&
    !previousVisit &&
    !nextInspectionMonth &&
    !nextInspectionWindow
  ) {
    return undefined;
  }

  return {
    totalAmmoniaKg,
    ammoniaWithinAuthorization,
    previousVisit,
    nextInspectionMonth,
    nextInspectionWindow,
  };
}

export function buildDefaultUnitChecks(site?: Site): VisitUnitCheck[] {
  const units = site?.refrigerationUnits ?? [];

  return units.map((unit, index) => ({
    id: unit.id ?? `unit-check-${index + 1}`,
    unitId: unit.id ?? `unit-${index + 1}`,
    unitName: unit.name?.trim() || unit.refrigerationUnitId?.trim() || `Impianto ${index + 1}`,
    refrigeration: {},
    safety: {
      sensors: {},
      reliefValves: {},
      shutoffValves: {},
      extractors: {},
      scrubber: {},
      abatementSystem: {},
      emergencyShutoff: {},
      alarmPanel: {},
      dialer: {},
    },
  }));
}
